import { inject, Injectable } from '@angular/core';
import { Study } from '@models';
import { StudyFilterService } from '@services';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ChartDataPoint {
  name: string;
  value: number;
  field: string;
  isFiltered: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ChartFilterIntegrationService {
  private filterService = inject(StudyFilterService);

  /**
   * Get chart data that reflects current filter state
   * This will update automatically when filters change
   */
  getTherapeuticAreaChartData(): Observable<ChartDataPoint[]> {
    return this.filterService.filterState$.pipe(
      map((filterState) => {
        const data = filterState.filteredData;
        const counts = this.groupByField(data, 'therapeuticArea');
        const appliedFilters = filterState.appliedFilters['therapeuticArea'] || new Set();

        return Object.entries(counts).map(([name, value]) => ({
          name,
          value,
          field: 'therapeuticArea',
          isFiltered: appliedFilters.has(name)
        }));
      })
    );
  }

  /**
   * Get study phase chart data that reflects current filter state
   */
  getStudyPhaseChartData(): Observable<ChartDataPoint[]> {
    return this.filterService.filterState$.pipe(
      map((filterState) => {
        const data = filterState.filteredData;
        const counts = this.groupByField(data, 'studyPhase');
        const appliedFilters = filterState.appliedFilters['studyPhase'] || new Set();

        return Object.entries(counts).map(([name, value]) => ({
          name,
          value,
          field: 'studyPhase',
          isFiltered: appliedFilters.has(name)
        }));
      })
    );
  }

  /**
   * Get study status chart data that reflects current filter state
   */
  getStudyStatusChartData(): Observable<ChartDataPoint[]> {
    return this.filterService.filterState$.pipe(
      map((filterState) => {
        const data = filterState.filteredData;
        const counts = this.groupByField(data, 'studyStatus');
        const appliedFilters = filterState.appliedFilters['studyStatus'] || new Set();

        return Object.entries(counts).map(([name, value]) => ({
          name,
          value,
          field: 'studyStatus',
          isFiltered: appliedFilters.has(name)
        }));
      })
    );
  }

  /**
   * Handle chart element click - toggle filter
   */
  onChartElementClick(field: string, value: string): void {
    this.filterService.toggleFilter(field, value, 'chart');
  }

  /**
   * Handle chart element double-click - filter to only this value
   */
  onChartElementDoubleClick(field: string, value: string): void {
    // Clear existing filters for this field and set only this value
    this.filterService.clearFieldFilters(field, 'chart');
    this.filterService.addFilter(field, value, 'chart');
  }

  /**
   * Handle chart background click - clear filters for this field
   */
  onChartBackgroundClick(field: string): void {
    this.filterService.clearFieldFilters(field, 'chart');
  }

  /**
   * Check if a specific value is currently filtered
   */
  isValueFiltered(field: string, value: string): boolean {
    return this.filterService.isFilterApplied(field, value);
  }

  /**
   * Group data by a specific field and count occurrences
   */
  private groupByField(data: Study[], field: keyof Study): Record<string, number> {
    return data.reduce(
      (acc, study) => {
        const value = study[field]?.toString() || 'Unknown';
        acc[value] = (acc[value] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }
}
